<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="drag-wrap"
      :class="{ 'is-dragging': isDragging }"
      :style="wrapStyle"
      @mousedown="handleMouseDown"
    >
      <div class="drag-wrap__content">
        <slot></slot>
      </div>
    </div>
  </transition>
</template>

<script>
import zIndexManager from "../../utils/zIndexManager";
export default {
  name: "DragWrap",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    modalId: {
      type: String,
      default: "",
    },
    modalName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      windowWidth: 0,
      windowHeight: 0,
      isInitialPosition: true,
      currentZIndex: 1000,
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 注册弹窗并获取 z-index
        if (this.modalId) {
          this.currentZIndex = zIndexManager.registerModal(
            this.modalId,
            this.modalName
          );
        }
        this.$nextTick(() => {
          this.updateWindowSize();
          this.centerElement();
        });
      } else {
        // 注销弹窗
        if (this.modalId) {
          zIndexManager.unregisterModal(this.modalId);
        }
      }
    },
  },
  computed: {
    wrapStyle() {
      const style = this.isInitialPosition
        ? {
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            cursor: this.isDragging ? "grabbing" : "grab",
            userSelect: "none",
            zIndex: this.currentZIndex,
          }
        : {
            position: "fixed",
            left: `${this.currentX}px`,
            top: `${this.currentY}px`,
            transform: "none",
            cursor: this.isDragging ? "grabbing" : "grab",
            userSelect: "none",
            zIndex: this.currentZIndex,
          };

      return style;
    },
  },
  mounted() {
    this.updateWindowSize();
    window.addEventListener("resize", this.updateWindowSize);
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);

    if (this.visible && this.modalId) {
      this.currentZIndex = zIndexManager.registerModal(
        this.modalId,
        this.modalName
      );
    }
  },
  beforeDestroy() {
    // 注销弹窗
    if (this.modalId) {
      zIndexManager.unregisterModal(this.modalId);
    }
    window.removeEventListener("resize", this.updateWindowSize);
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);
  },
  methods: {
    updateWindowSize() {
      const oldWindowWidth = this.windowWidth;
      const oldWindowHeight = this.windowHeight;
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;

      // If not in initial position, adjust position if outside new boundaries
      if (
        !this.isInitialPosition &&
        (oldWindowWidth !== this.windowWidth ||
          oldWindowHeight !== this.windowHeight)
      ) {
        this.$nextTick(() => {
          const element = this.$el;
          const rect = element.getBoundingClientRect();

          this.currentX = Math.min(
            Math.max(this.currentX, 0),
            this.windowWidth - rect.width
          );
          this.currentY = Math.min(
            Math.max(this.currentY, 0),
            this.windowHeight - rect.height
          );
        });
      }
    },
    centerElement() {
      this.$nextTick(() => {
        this.isInitialPosition = true;
        const element = this.$el;
        const rect = element.getBoundingClientRect();
        this.currentX = rect.left;
        this.currentY = rect.top;
      });
    },
    handleMouseDown(event) {
      // 如果点击的是 can-not-drag-wrap 内部，直接 return
      if (event.target.closest(".can-not-drag-wrap")) {
        return;
      }
      if (event.target.closest(".drag-wrap__content")) {
        event.preventDefault();
        this.isDragging = true;
        const rect = this.$el.getBoundingClientRect();
        this.currentX = rect.left;
        this.currentY = rect.top;
        this.isInitialPosition = false;
        this.startX = event.clientX - this.currentX;
        this.startY = event.clientY - this.currentY;
      }
    },
    handleMouseMove(event) {
      if (!this.isDragging) return;

      const newX = event.clientX - this.startX;
      const newY = event.clientY - this.startY;

      // 获取元素尺寸
      const element = this.$el;
      const rect = element.getBoundingClientRect();

      // 限制拖动范围，确保元素不会超出窗口边界
      // 重新计算最大允许的 left 和 top 值，确保 right 和 bottom 在窗口内
      const maxLeft = this.windowWidth - rect.width;
      const maxTop = this.windowHeight - rect.height;

      this.currentX = Math.min(
        Math.max(newX, 0),
        maxLeft > 0 ? maxLeft : 0 // 如果 maxLeft 小于0（元素比窗口宽），则最大left为0
      );
      this.currentY = Math.min(
        Math.max(newY, 0),
        maxTop > 0 ? maxTop : 0 // 如果 maxTop 小于0（元素比窗口高），则最大top为0
      );
    },
    handleMouseUp() {
      this.isDragging = false;
    },
    handleClick(event) {
      // 如果点击的是 can-not-drag-wrap 内部，不处理
      // if (event.target.closest(".can-not-drag-wrap")) {
      //   return;
      // }

      // 如果正在拖拽，不处理点击事件
      // if (this.isDragging) {
      //   return;
      // }

      // 将当前弹窗置于顶部
      if (this.modalId) {
        this.currentZIndex = zIndexManager.bringToFront(
          this.modalId,
          this.modalName
        );
        console.log(
          `[DragWrap] 弹窗 ${
            this.modalName || this.modalId
          } 已置于顶部，z-index: ${this.currentZIndex}`
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.drag-wrap {
  display: inline-block;
  border-radius: 12px;
  background: #27272a;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  will-change: transform;
  min-width: 50px;
  min-height: 50px;
  max-width: 90vw;
  max-height: 90vh;
  width: fit-content;
  // height: fit-content;
  -webkit-app-region: none;

  &.is-dragging {
    transition: none;
    box-shadow: 0 8px 12px -1px rgba(0, 0, 0, 0.2),
      0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  &__content {
    width: 100%;
    height: 100%;
    overflow: auto;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
    }
  }
  ::v-deep .can-not-drag-wrap {
    cursor: auto;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
